<template>
    <div class="col-lg-12">
        <div v-if="isLoaded">
            <div v-if="!hasRecords">
                <div class="text-center">
                    <h6>No hay registros disponibles para esta semana.</h6>
                </div>
            </div>
            <div v-if="hasRecords">
                <!-- Barra de búsqueda -->
                <div class="search-container">
                    <input type="text" v-model="searchQuery" placeholder="Buscar por proyecto, cliente o actividad..."
                        class="form-control" />
                </div>
                <!-- Acordeones agrupados por fecha -->
                <div class="accordion mt-3" id="timeRecordsAccordion">
                    <div v-for="([date, records], index) in paginatedRecords" :key="date" class="accordion-item">
                        <h2 class="accordion-header" :id="'heading' + index">
                            <button
                                class="accordion-button custom-header d-flex justify-content-between align-items-center"
                                :class="{ collapsed: !isOpen[index] }"
                                type="button"
                                @click="toggleAccordion(index)">
                                <span class="date-title d-flex align-items-center">
                                    {{ formatDate3(date) }}
                                    <i :class="isOpen[index] ? 'ni ni-bold-up ms-2' : 'ni ni-bold-down ms-2'"></i>
                                </span>
                            </button>
                        </h2>
                        <div :id="'collapse' + index" 
                             class="accordion-collapse collapse"
                             :class="{ show: isOpen[index] }"
                             :aria-labelledby="'heading' + index">
                            <div class="accordion-body">
                                <ul class="list-group">
                                    <li class="list-group-item record-item" v-for="(record, i) in records" :key="i">
                                        <div class="record-details">
                                            <p><strong>Horas:</strong> {{ record.total_hours }}</p>
                                            <p><strong>Proyecto:</strong> {{ record.project }}</p>
                                            <p><strong>Cliente:</strong> {{ record.client }}</p>
                                            <p><strong>Actividad:</strong> {{ record.activity }}</p>
                                            <p><strong>Es tiempo Extra?:</strong> {{ record.is_extra_time ? 'SI' : 'NO'
                                            }}</p>
                                        </div>
                                        <div v-if="record.is_auth || record.sent" class="d-flex flex-column gap-2">
                                            <span :class="record.is_auth? 'badge badge-success' : 'badge badge-warning'">
                                                {{ record.is_auth? 'Autorizado' : 'Enviado'}}
                                            </span>
                                        </div>
                                        <div v-else class="d-flex flex-column gap-2">
                                            <button class="btn btn-danger mb-1" @click="removeRecord(record.time_record_id)">
                                                Borrar
                                            </button>
                                            <button class="btn btn-secondary mb-0" @click="editRecord(record.time_record_id)">
                                                Editar
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Paginación -->
                <nav class="pagination-container mt-4">
                    <ul class="pagination">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" @click="currentPage > 1 && currentPage--">«</a>
                        </li>
                        <li class="page-item" v-for="page in totalPages" :key="page"
                            :class="{ active: currentPage === page }">
                            <a class="page-link" @click="currentPage = page">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" @click="currentPage < totalPages && currentPage++">»</a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
        <div v-if="!isLoaded">
            <div class="text-center">
                Cargando...
            </div>
        </div>
    </div>
    <Modal :isOpen="isModalOpen" :title="modalTitle" :selectedFormComponent="selectedFormComponent"
        :selectedData="selectedData" @close="closeModal">
    </Modal>
</template>

<script setup>
import { watch, computed, ref, onMounted, shallowRef } from 'vue';

const props = defineProps({
    startDate: Date,
    endDate: Date,
});

const formatToLocalYMD = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    // Ajuste: sumar 1 día para evitar el desplazamiento UTC
    d.setDate(d.getDate());

    return d.toLocaleDateString('sv-SE'); // formato YYYY-MM-DD
};

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = 10;

const time_records = ref([])
const isLoaded = ref(false)
const hasRecords = ref(false)

// Estado para controlar qué acordeón está abierto
const isOpen = ref({});

// Función para alternar el estado del acordeón
const toggleAccordion = (index) => {
    isOpen.value[index] = !isOpen.value[index];
};

import Modal from '../Modal.vue';
import EditTimeRecord from './EditTimeRecord.vue';

const formComponents = { EditTimeRecord: EditTimeRecord }

const isModalOpen = ref(false);
const selectedFormComponent = shallowRef(null);
const modalTitle = ref("");
const selectedData = ref(null)

// Función para abrir el modal con un formulario y datos específicos
const openModal = (data, formType) => {
    selectedData.value = data || {}; // Si no se pasa nada, se inicializa como objeto vacío
    selectedFormComponent.value = formComponents[formType] || null;

    // Asignar título dinámico
    const titles = {
        EditTimeRecord: "Editar Registro",
    };
    modalTitle.value = titles[formType] || "Editar Información";
    isModalOpen.value = true;
};

const closeModal = () => {
    isModalOpen.value = false;
};

import axios from 'axios';
const API_BASE_URL = process.env.VUE_APP_BASE_URL;
const token = localStorage.getItem('authToken'); // Obtén el token del localStorage

const fetchTimeRecords = async () => {

    const body = {
        start_date: formatToLocalYMD(props.startDate),
        end_date: formatToLocalYMD(props.endDate),
    }

    try {
        const response = await axios.post(`${API_BASE_URL}/user-time-records-per-week`, body, {
            headers: {
                Authorization: `Bearer ${token}`, // Agrega el token en el header
            },
        });
        if (response.status == 200) {
            time_records.value = response.data; // Asigna los datos a variable `time_records`
            console.log(time_records.value)
            isLoaded.value = true
            if (time_records.value.length > 0) {
                hasRecords.value = true
            } else {
                hasRecords.value = false
            }
        }

    } catch (error) {
        console.error('Error al obtener los datos:', error);
    }
};

// Computed para agrupar registros por fecha y filtrar por búsqueda
const groupedRecords = computed(() => {
    // Filtrar registros por el término de búsqueda
    const filteredRecords = time_records.value.filter(record =>
        record.project.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        record.client.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        record.activity.toLowerCase().includes(searchQuery.value.toLowerCase())
    );

    // Agrupar registros por fecha
    return filteredRecords.reduce((acc, record) => {
        const date = record.date;
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(record);
        return acc;
    }, {});
});

// Paginación
const totalPages = computed(() => Math.ceil(Object.keys(groupedRecords.value).length / itemsPerPage));
const paginatedRecords = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return Object.entries(groupedRecords.value).slice(start, end);
});

// Formato de fecha con día de la semana
function formatDate3(dateString) {
    if (!dateString) return ''; // Evita errores si la fecha es null o undefined

    const d = new Date(dateString); // Convierte la cadena en un objeto Date
    if (isNaN(d.getTime())) return ''; // Verifica si la fecha es válida

    // Días de la semana en español
    const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

    // Usamos getUTC*() para obtener la fecha en UTC
    const dayOfWeek = diasSemana[d.getUTCDay()]; // Día de la semana
    const day = String(d.getUTCDate()).padStart(2, '0'); // Día en 2 dígitos
    const month = String(d.getUTCMonth() + 1).padStart(2, '0'); // Mes en 2 dígitos
    const year = d.getUTCFullYear(); // Año en 4 dígitos

    return `${dayOfWeek} ${day}/${month}/${year}`; // Formato: Día de la semana, dd/mm/yyyy
}

const removeRecord = async (recordId) => {
    if (confirm('¿Estás segur@ de eliminar este registro?')) {
        try {
            const response = await axios.post(`${API_BASE_URL}/remove-time-record`, { time_record_id: recordId }, {
                headers: {
                    Authorization: `Bearer ${token}`, // Agrega el token en el header
                },
            });
            if (response.status === 200) {
                alert(`Registro Eliminado`);
                // Actualiza los datos
                await fetchTimeRecords();
            }
        } catch (error) {
            console.error('Error al registrar la actividad:', error);
            // Verificar si hay respuesta del servidor
            if (error.response) {
                alert(error.response.data.msg || 'Ocurrió un error al registrar el evento.');
            } else {
                alert('Error de conexión con el servidor.');
            }
        }
    }
};

const editRecord = (recordId) => {
    const formType = 'EditTimeRecord'
    const data = { time_record_id: recordId }
    openModal(data, formType)
}

watch(() => [props.startDate, props.endDate], fetchTimeRecords, { immediate: true })

// Obtener registros al montar el componente
onMounted(async () => {
    await fetchTimeRecords();
});
</script>

<style scoped>
/* Estilos para la barra de búsqueda */
.search-container {
    display: flex;
    justify-content: center;
}

.form-control {
    max-width: 400px;
    border-radius: 20px;
    padding: 8px 15px;
}

/* Estilos de los acordeones */
.accordion-item {
    border: 1px solid #ddd;
    margin-bottom: 10px;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
}

.accordion-button {
    background: white;
    color: gray;
    font-weight: bold;
    border: none;
    transition: background 0.3s ease;
    cursor: pointer;
}

.accordion-button:hover {
    background: #01a5f5;
    color: white;
}

.accordion-button:not(.collapsed) {
    background: #01a5f5;
    color: white;
}

.date-title {
    font-size: 16px;
}

/* Estilos de los registros */
.record-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #eee;
}

.record-details p {
    margin: 0;
    font-size: 16px;
    color: #333;
}

/* Estilos de paginación */
.pagination-container {
    display: flex;
    justify-content: center;
}

.pagination {
    display: flex;
    gap: 5px;
}

.page-item .page-link {
    border-radius: 5px;
    color: #01a5f5;
    cursor: pointer;
}

.page-item.active .page-link {
    background-color: #01a5f5;
    border-color: #01a5f5;
    color: white;
}

.page-item.disabled .page-link {
    color: #bbb;
    cursor: not-allowed;
}

.accordion-button i {
    transition: transform 0.3s;
}

/* Animación del colapso */
.accordion-collapse {
    transition: height 0.3s ease;
}
</style>